import { useCallback, useEffect, useState } from 'react'
import { adminApi, type ProviderAccountOpeningApplication } from '../api/admin'
import { providersApi, type Provider } from '../api/providers'

interface Props {
  onPendingCountChange?: (count: number) => void
}

const statusStyle: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING_INTERNAL: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  SENT: 'bg-blue-100 text-blue-800',
  EXTERNAL_PENDING: 'bg-indigo-100 text-indigo-800',
  RETURNED: 'bg-orange-100 text-orange-800',
  DECLINED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-slate-100 text-slate-600',
}

export default function ProviderAccountOpeningApplicationsPanel({ onPendingCountChange }: Props) {
  const [applications, setApplications] = useState<ProviderAccountOpeningApplication[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [loading, setLoading] = useState(true)
  const [actionId, setActionId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [reasonById, setReasonById] = useState<Record<string, { code: string; comment: string }>>({})

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const [applicationsResponse, providersResponse] = await Promise.all([
        adminApi.getProviderAccountOpeningApplications(),
        providersApi.getAll(),
      ])
      const data = applicationsResponse.data || []
      setApplications(data)
      setProviders(providersResponse)
      onPendingCountChange?.(data.filter((item) => item.status === 'PENDING_INTERNAL').length)
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load account opening applications')
    } finally {
      setLoading(false)
    }
  }, [onPendingCountChange])

  useEffect(() => {
    load()
  }, [load])

  const execute = async (id: string, action: () => Promise<unknown>) => {
    try {
      setActionId(id)
      setError('')
      await action()
      await load()
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'The action could not be completed')
    } finally {
      setActionId(null)
    }
  }

  const reason = (id: string) => reasonById[id] || { code: '', comment: '' }
  const setReason = (id: string, key: 'code' | 'comment', value: string) => {
    setReasonById((current) => ({ ...current, [id]: { ...reason(id), [key]: value } }))
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Provider account opening applications</h3>
        <p className="mt-1 text-sm text-gray-500">Provider registration is available only after internal Compliance approval.</p>
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">Loading applications…</div>
      ) : applications.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">No provider account opening applications</div>
      ) : (
        <div className="divide-y divide-gray-200">
          {applications.map((application) => {
            const selectedReason = reason(application.id)
            const isBusy = actionId === application.id
            return (
              <div key={application.id} className="px-6 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs text-gray-500">{application.id}</div>
                    <div className="mt-1 text-sm font-medium text-gray-900">
                      {application.items.map((item) => item.currencyId).join(', ')}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{new Date(application.createdAt).toLocaleString()}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle[application.status] || 'bg-gray-100 text-gray-700'}`}>
                    {application.status}
                  </span>
                </div>
                <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="text-sm text-gray-600">
                    {application.processingMode && <span>Mode: {application.processingMode} · </span>}
                    {application.reasonComment || application.purpose || 'No client comment'}
                  </div>
                  {application.status === 'DRAFT' || application.status === 'RETURNED' ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={application.providerId || ''}
                        onChange={(event) => execute(application.id, () => adminApi.selectProviderAccountOpeningApplicationProvider(application.id, event.target.value))}
                        disabled={isBusy}
                        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                      >
                        <option value="">Select provider</option>
                        {providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
                      </select>
                      {application.providerId && (
                        <button disabled={isBusy} onClick={() => execute(application.id, () => adminApi.submitProviderAccountOpeningApplication(application.id))} className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
                          Submit review
                        </button>
                      )}
                    </div>
                  ) : null}
                  {application.status === 'PENDING_INTERNAL' ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input value={selectedReason.code} onChange={(event) => setReason(application.id, 'code', event.target.value)} placeholder="Reason code" className="w-32 rounded border border-gray-300 px-2 py-1.5 text-sm" />
                      <input value={selectedReason.comment} onChange={(event) => setReason(application.id, 'comment', event.target.value)} placeholder="Comment" className="w-40 rounded border border-gray-300 px-2 py-1.5 text-sm" />
                      <button disabled={isBusy} onClick={() => execute(application.id, () => adminApi.approveProviderAccountOpeningApplication(application.id))} className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Approve</button>
                      <button disabled={isBusy || !selectedReason.code} onClick={() => execute(application.id, () => adminApi.returnProviderAccountOpeningApplication(application.id, selectedReason.code, selectedReason.comment || undefined))} className="rounded bg-orange-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Return</button>
                      <button disabled={isBusy || !selectedReason.code} onClick={() => execute(application.id, () => adminApi.declineProviderAccountOpeningApplication(application.id, selectedReason.code, selectedReason.comment || undefined))} className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Decline</button>
                    </div>
                  ) : null}
                  {application.status === 'APPROVED' && (
                    <button disabled={isBusy} onClick={() => execute(application.id, () => adminApi.sendProviderAccountOpeningApplication(application.id))} className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
                      {application.processingMode === 'API' ? 'Register and send' : 'Mark sent to provider'}
                    </button>
                  )}
                </div>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-medium text-gray-500">Package preview</summary>
                  <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-3 text-xs text-gray-600">{application.packageSnapshotJson}</pre>
                </details>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
